import { useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';

import { Button } from '@actual-app/components/button';
import { Text } from '@actual-app/components/text';
import { View } from '@actual-app/components/view';

import { Boton } from '#components/mobile/ui/Boton';
import { Hoja } from '#components/mobile/ui/Hoja';
import { Icono } from '#components/mobile/ui/Icono';
import { Tarjeta } from '#components/mobile/ui/Tarjeta';
import { color, espacio, texto } from '#components/mobile/ui/tokens';

import { BocadilloGavi } from './BocadilloGavi';
import type { ErrorLectura } from './leerArchivo';

type PasoElegirProps = {
  nombreCuenta: string;
  error: ErrorLectura | null;
  onElegir: () => void;
};

/** Paso 1: qué archivo traer y el botón para elegirlo. */
export function PasoElegir({ nombreCuenta, error, onElegir }: PasoElegirProps) {
  const { t } = useTranslation();
  const [ayuda, setAyuda] = useState(false);

  const formatos = [
    { nombre: 'CSV', detalle: t('text') },
    { nombre: 'Excel', detalle: '.xls · .xlsx' },
    { nombre: 'Norma 43', detalle: '.n43 · .txt' },
  ];

  const mensajeError: Record<ErrorLectura, string> = {
    formato: t("I can't read that kind of file. Try CSV, Excel or Norma 43."),
    'excel-antiguo': t(
      'That Excel is the old kind. Open it and save it as .xlsx or CSV, or download it again in another format.',
    ),
    vacio: t('That file has no transactions I can recognise.'),
    ilegible: t(
      "I couldn't read that file. Try downloading it again, or in another format.",
    ),
  };

  return (
    <View style={{ flex: 1, gap: 18, padding: espacio.margen }}>
      <BocadilloGavi
        estado={error ? 'ladea' : 'reposo'}
        testId="importar-bocadillo"
        ayudaError={error ?? undefined}
      >
        {error
          ? mensajeError[error]
          : t(
              "Download the transactions from your bank's website or app and bring them here. I'll put them in {{account}} without repeating the ones you already have.",
              { account: nombreCuenta },
            )}
      </BocadilloGavi>

      <Tarjeta style={{ gap: 12 }} data-testid="importar-formatos">
        <Text style={{ ...texto.etiqueta, color: color.fg2 }}>
          <Trans>Any of these works</Trans>
        </Text>
        <View style={{ flexDirection: 'row', gap: 8 }}>
          {formatos.map(f => (
            <View
              key={f.nombre}
              style={{
                flex: 1,
                alignItems: 'center',
                padding: '12px 6px',
                borderRadius: 12,
                backgroundColor: color.surface2,
              }}
            >
              <Text style={{ ...texto.fila, fontWeight: 800 }}>{f.nombre}</Text>
              <Text style={{ fontSize: 11, color: color.fg2, marginTop: 2 }}>
                {f.detalle}
              </Text>
            </View>
          ))}
        </View>
        <Text style={{ ...texto.secundario, color: color.fg2 }}>
          <Trans>Also OFX, QIF and CAMT, if your bank offers them.</Trans>
        </Text>
      </Tarjeta>

      <Tarjeta
        onPress={() => setAyuda(true)}
        data-testid="importar-ayuda"
        style={{ flexDirection: 'row', alignItems: 'center', gap: 10 }}
      >
        <View style={{ flex: 1, gap: 2 }}>
          <Text style={texto.fila}>
            <Trans>How do I download it from my bank?</Trans>
          </Text>
          <Text style={{ ...texto.secundario, color: color.fg2 }}>
            <Trans>Where it usually is on your bank's website</Trans>
          </Text>
        </View>
        <Icono nombre="cr" size={18} style={{ color: color.fg2 }} />
      </Tarjeta>

      <Text
        style={{ ...texto.secundario, color: color.fg2, textAlign: 'center' }}
        data-testid="importar-privacidad"
      >
        <Trans>
          The file is read on your phone and isn't kept anywhere. Only the
          transactions stay.
        </Trans>
      </Text>

      <View style={{ flex: 1 }} />

      <Boton bloque onPress={onElegir} data-testid="importar-elegir">
        <Icono nombre="download" size={20} />
        <Trans>Choose file</Trans>
      </Boton>

      {ayuda && (
        <Hoja
          aria-label={t('How do I download it from my bank?')}
          onCerrar={() => setAyuda(false)}
        >
          <View style={{ gap: 12, paddingBottom: 8 }}>
            <Text style={texto.titulo}>
              <Trans>How do I download it from my bank?</Trans>
            </Text>
            {[
              t(
                "Open your bank's website (it's easier than the app) and go into the account.",
              ),
              t(
                'Find its transactions and look for «Download», «Export» or the download icon.',
              ),
              t(
                'Choose the dates and the format: Excel, CSV or Norma 43 (it may be called «Cuaderno 43» or «AEB 43»).',
              ),
              t(
                'On your phone, the file ends up in Downloads: choose it from there.',
              ),
            ].map((paso, i) => (
              <View key={i} style={{ flexDirection: 'row', gap: 10 }}>
                <Text
                  style={{
                    width: 26,
                    height: 26,
                    borderRadius: 13,
                    flexShrink: 0,
                    textAlign: 'center',
                    lineHeight: '26px',
                    fontWeight: 800,
                    color: color.accentInk,
                    backgroundColor: color.accent,
                  }}
                >
                  {i + 1}
                </Text>
                <Text style={{ ...texto.cuerpo, flex: 1 }}>{paso}</Text>
              </View>
            ))}
            <Button
              variant="bare"
              onPress={() => setAyuda(false)}
              style={{ minHeight: 44, fontWeight: 700, color: color.accent }}
            >
              <Trans>Got it</Trans>
            </Button>
          </View>
        </Hoja>
      )}
    </View>
  );
}
